import type { Draft } from 'immer';
import { getSessionEvents } from '@/db/session-events';
import type { NewSessionEvent, RoundId, Session, SessionEvent, SessionId } from '@/schemas';
import { SCHEMA_VERSIONS } from '@/schemas/versions';
import { replaySessionEvents } from './event-replay';
import { getUndoableEvent, getRedoableEvent } from './undo-redo';

/**
 * Create a system event with payload validation
 *
 * When called with a literal event type, the payload should match the schema
 * for that event type. Runtime validation via Zod schemas (in addSessionEvent)
 * ensures correctness.
 *
 * @param sessionId - Session ID
 * @param type - Event type (literal string for best type inference)
 * @param payload - Event payload (must match schema for event type, validated at runtime)
 * @param userId - Optional user ID
 * @returns NewSessionEvent
 *
 * @example
 * ```ts
 * // Usage with literal event type (TypeScript will provide better inference)
 * createSystemEvent(sessionId, 'SessionNameChanged', { name: 'New Name' }, userId)
 *
 * // Runtime validation ensures payload matches the schema for the event type
 * createSystemEvent(sessionId, 'SessionEnded', {}, userId)
 * ```
 */
export function createSystemEvent<TEventType extends NewSessionEvent['type']>(
  sessionId: SessionId,
  type: TEventType,
  payload: Record<string, unknown>,
  userId?: NewSessionEvent['userId'],
): NewSessionEvent {
  return {
    sessionId,
    type,
    userId,
    version: SCHEMA_VERSIONS.SESSION_EVENT,
    payload,
  };
}

/**
 * Validate that a session has one of the allowed statuses
 */
export function ensureSessionStatus(session: Session, allowedStatuses: Session['status'][]): void {
  if (!allowedStatuses.includes(session.status)) {
    const formattedStatuses = allowedStatuses.join(' or ');
    throw new Error(`Session must be ${formattedStatuses} to perform this action`);
  }
}

/**
 * Find a round by ID or throw an error
 */
export function findRoundOrThrow(session: Session, roundId: RoundId) {
  const round = session.rounds.find((r) => r.id === roundId);
  if (!round) {
    throw new Error('Round not found');
  }
  return round;
}

/**
 * Compute undo/redo availability from events
 */
export function computeUndoRedoAvailability(events: SessionEvent[]) {
  const undoableEvent = getUndoableEvent(events);
  const redoableEvent = getRedoableEvent(events);
  return {
    canUndo: undoableEvent !== undefined,
    canRedo: redoableEvent !== undefined,
  };
}

/**
 * Get active session from store or throw error
 */
export function createRequireActiveSession(get: () => { activeSession: Session | undefined }) {
  return (): Session => {
    const session = get().activeSession;
    if (!session) {
      throw new Error('No active session');
    }
    return session;
  };
}

/**
 * Session recovery context - dependencies needed for recovery
 */
interface SessionRecoveryContext {
  get: () => { activeSession: Session | undefined };
  set: (
    updater: (
      draft: Draft<{ activeSession: Session | undefined; canUndo: boolean; canRedo: boolean }>,
    ) => void,
  ) => void;
}

/**
 * Create a session recovery handler
 * Handles refreshing session state from events after failed operations
 */
export function createSessionRecovery(context: SessionRecoveryContext) {
  return async (sessionId: SessionId): Promise<void> => {
    const events = await getSessionEvents(sessionId);
    const latestSession = replaySessionEvents(events);
    const availability = computeUndoRedoAvailability(events);
    context.set((draft) => {
      if (draft.activeSession?.id === sessionId) {
        draft.activeSession = latestSession;
        draft.canUndo = availability.canUndo;
        draft.canRedo = availability.canRedo;
      }
    });
  };
}
